import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { usePostDeletionSurvey } from "../../../../components/PostDeletionSurvey";

import SearchBar from "../../../../components/SearchBar";
import PaginationAds from "../PaginationAds";
import { LocalStorageKey, PageRoute, QueryParam, PromotionFromPages, START_PAGE_PARAM } from "../../../../models/enums";
import {
  selectMyAds,
  getMyAds,
  myAdsDeleteAdAction,
  activeAd,
  pausedAd,
  soldAd,
  updateMyAds,
  renewAd,
  enableAdAutoPromotion,
  disableAdAutoPromotion,
  changeAdPrice
} from "../../../../routes/Ad/ad.ducks";
import {
  checkEmptyObject,
  formatDate,
  localStorage,
  notification
} from "../../../../services";
import config from "appConfig";
import history from "../../../../history";
import { selectModules } from "../../../App/selectors";

import styles from "./tabContent.scss";
import {
  AdListFavoriteDataI,
  ModulesI
} from "../../../../typings/routesTypes";
import { rootReducerInterface } from "../../../../typings/interfaces";
import { SearchCriteriaI } from "../Tabs";
import { getUserProfile } from "../../../../routes/Auth/auth.ducks";
import ReactGA from "react-ga4";
import confirmModal from "../../../../services/confirmModal";

interface TabContentProps {
  AdStatusOptions: { name: string; value: number }[];
  activeAd: Function;
  ads: {
    count?: number;
    data?: AdListFavoriteDataI;
    nextPage?: number;
    page?: number;
    actionTime?: number;
  };
  getMyAds: Function;
  pausedAd: Function;
  modules: ModulesI;
  myAdsDeleteAdAction: Function;
  onTabChange: Function;
  renewAd: Function;
  soldAd: Function;
  enableAdAutoPromotion: Function;
  disableAdAutoPromotion: Function;
  changeAdPrice: Function;
  statistic: number[];
  status: number;
  updateMyAds: Function;
  updateStatistics: Function;
  searchCriteria: SearchCriteriaI;
  onModuleSearch: Function;
  onTextSearch: Function;
  onPromotionStatusSearch: Function;
  onSortOptionChange: Function;
  AdPromotionFilterOptions: { name: string; value: number }[];
  getUserProfile: Function;
  deleteAdTrigger: string | null;
  renewAdTriggerId: string | null;
  renewAdTriggerFromEmail?: boolean;
}

const readPageFromUrl = (): number => {
  const raw = new URLSearchParams(history.location.search).get(QueryParam.page);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return parsed > 0 ? parsed : 1;
};

const readLastViewedAdIdFromState = () => {
  const state: any = history.location.state;
  return state && state.lastViewedAdId ? state.lastViewedAdId : null;
};

const syncPageToUrl = (nextPage: number): void => {
  const params = new URLSearchParams(history.location.search);
  if (nextPage > 1) {
    params.set(QueryParam.page, String(nextPage));
  } else {
    params.delete(QueryParam.page);
  }
  history.replace({
    pathname: history.location.pathname,
    search: params.toString(),
  });
};

const TabContent: React.FC<TabContentProps> = props => {
  const {
    ads,
    status,
    modules,
    onTabChange,
    statistic,
    searchCriteria,
    onModuleSearch,
    onTextSearch,
    onPromotionStatusSearch,
    onSortOptionChange,
    deleteAdTrigger,
    renewAdTriggerId,
    renewAdTriggerFromEmail,
  } = props;


  const pageFromUrl = readPageFromUrl()
  const [page, setPage] = useState(pageFromUrl);
  const { showPostDeletionSurvey } = usePostDeletionSurvey();
  // One-shot scroll target picked up from history entry state on mount;
  // cleared as soon as we scroll (or when the target is no longer visible).
  const pendingScrollAdIdRef = useRef(readLastViewedAdIdFromState());

  // ensure that latest response is displayed by comparing actionTime with ads.actionTime in useEffect hook below
  const [tempAds, setTempAds] = useState(undefined);
  const [actionTime, setActionTime] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadMyAds(props, page, searchCriteria);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [JSON.stringify(searchCriteria)]);

  useEffect(() => {
    if (deleteAdTrigger) {
      handleDeleteClick(deleteAdTrigger)
    }
  }, [deleteAdTrigger])

  useEffect(() => {
    if (renewAdTriggerId) {
      // Re-read utm_source directly instead of trusting the prop — React 16
      // doesn't batch setState calls fired from the parent's useEffect, so
      // by the time this effect runs the renewAdTriggerId prop can already
      // be set while renewAdTriggerFromEmail is still at its default
      // (false), causing the toast flow instead of the email-landing modal.
      const urlParams = new URLSearchParams(window.location.search);
      const isFromEmail = urlParams.get("utm_source") === "email";
      handleRenewClick(renewAdTriggerId, isFromEmail)
    }
  }, [renewAdTriggerId])

  const loadMyAds = (
    props?: TabContentProps,
    nextPage?: number | undefined,
    searchCriteria?: SearchCriteriaI
  ) => {
    const timestamp = new Date().getTime();
    props.updateMyAds({});
    props.getMyAds(nextPage, searchCriteria, timestamp);
    setActionTime(timestamp);
  };


  const loadNewData = (adId: string) => {
    if (adId) {
      getUserProfile({}, null, true);
      props.updateStatistics(true);
      loadMyAds(props, page, searchCriteria);
    }
  };

  const handleDeleteClick = (adId: string) => {
    // Capture category before the ad is removed from state — the survey
    // opens in the delete callback, when the ad is no longer in props.ads.
    const adList = props.ads && props.ads.data;
    const adBeforeDelete = Array.isArray(adList) ? adList.find(a => a.id === adId) : null;
    const adCategory = adBeforeDelete ? adBeforeDelete.category : null;

    props.myAdsDeleteAdAction(adId, props.ads, (deletedAdId) => {
      if (deletedAdId) {
        notification.show("Oglas je obrisan.");
      }

      loadNewData(deletedAdId);

      showPostDeletionSurvey(deletedAdId, adCategory);
    });
  };

  const handleEditClick = (adId: string, module: string) => {
    localStorage.removeItem(LocalStorageKey.savedForm);
    history.push(`${PageRoute.editAd}/${adId}`);
  };

  const handleActiveClick = (adId: string) => {
    props.activeAd(adId, props.ads, (adId) => {
      if (adId) {
        notification.show("Oglas je aktiviran.");
      }
      loadNewData(adId);
    });
  };

  const handleRenewClick = (adId: string, isFromEmail?: boolean) => {
    props.renewAd(adId, (response: any) => {
      if (!response) {
        // Email landings surface the failure (link stale, ad deleted, too old,
        // not the user's ad, …) so the user doesn't stare at a blank list.
        // In-app renew buttons stay silent — the user kicked it off and will
        // notice the row didn't change.
        if (isFromEmail) {
          notification.showError(
            "Obnova oglasa nije uspjela. Oglas možda više ne postoji ili se ne može obnoviti."
          );
        }
        return;
      }

      if (isFromEmail) {

        // Email-landing confirmation modal (V2OGLASI-2646). Replaces the toast
        // so the user clearly sees the renewal happened and can jump into the
        // promote flow in one click. Page-position line only when known.
        //
        // BE returns title / expirationDate / pagePosition on /aditem/renew.
        // Fall back to the ad row cached in redux for the title (unchanged by
        // renewal) when the response is missing it — covers environments
        // where BE hasn't shipped the enriched response yet.
        const adList = props.ads && props.ads.data;
        const localAd = Array.isArray(adList)
          ? adList.find((a: any) => a.id === adId)
          : null;
        const adTitle = response.title || (localAd && localAd.title) || null;
        const expirationFormatted = response.expirationDate
          ? formatDate(response.expirationDate, config.dateFormat)
          : null;
        const pagePositionLine = response.pagePosition > 0
          ? `Trenutno se nalazi na ${response.pagePosition}. stranici kategorije. `
          : "";

        ReactGA.event({
          category: "Promocija oglasa nakon obnove",
          action: "Click",
          label: 'Prikazan modal za obnovu iz emaila'
        });

        confirmModal.show({
          title: "Oglas je uspješno obnovljen",
          okText: "Istakni oglas",
          cancelText: "Nastavi bez isticanja",
          iconType: "NaN",
          onCancel: () => {
            ReactGA.event({
              category: "Promocija oglasa nakon obnove",
              action: "Click",
              label: 'Nastavak bez isticanja iz emaila'
            });
          },
          onOk: () => {
            ReactGA.event({
              category: "Promocija oglasa nakon obnove",
              action: "Click",
              label: 'Isticanje oglasa iz emaila'
            });

            handlePromoteClick(adId);
          },
          children: (
            <React.Fragment>
              {adTitle && <p><strong>{adTitle}</strong></p>}
              <p>
                {/* V2OGLASI-2646 QA: dateFormat already ends with a period
                    (`D.M.YYYY.`), so appending another `.` here produced
                    a visible `26.11.2026..`. The date's own period
                    terminates the sentence; we only need a space before
                    the next sentence. */}
                {expirationFormatted
                  ? <React.Fragment>Vaš oglas je obnovljen i aktivan je do <strong>{expirationFormatted}</strong> </React.Fragment>
                  : "Vaš oglas je obnovljen. "}
                {pagePositionLine}
              </p>
              <p>
                Isticanjem oglasa možete ostvariti veću vidljivost i bolje rezultate.
              </p>
            </React.Fragment>
          )
        });

        loadNewData(adId);
        return;
      }

      notification.show("Oglas je obnovljen.");

      if (response.pagePosition > 5) {

        ReactGA.event({
          category: "Promocija oglasa nakon obnove",
          action: "Click",
          label: 'Prikazan modal za obnovu'
        });


        confirmModal.show({
          title: "Istaknite oglas",
          okText: "Istakni oglas",
          cancelText: "Odustani",
          iconType: "NaN",
          onCancel: () => {
            ReactGA.event({
              category: "Promocija oglasa nakon obnove",
              action: "Click",
              label: 'Korisnik odustao'
            });
          },
          onOk: () => {
            ReactGA.event({
              category: "Promocija oglasa nakon obnove",
              action: "Click",
              label: 'Želi promociju'
            });

            handlePromoteClick(adId);
          },
          children: <span><strong>Vaš oglas je trenutno na {response.pagePosition}. stranici.</strong> <br />Isticanjem će se prikazivati na prvoj stranici, iznad ostalih oglasa u kategoriji.</span>
        });

      }


      loadNewData(adId);

    })
  };

  const handlePausedClick = (adId: string) => {
    props.pausedAd(adId, props.ads, (adId) => {
      notification.show("Oglas je pauziran.");
      loadNewData(adId);
    });
  };

  const handleSoldClick = (adId: string) => {
    props.soldAd(adId, props.ads, (adId) => {
      if (adId) {
        notification.show("Oglas je označen kao prodan.");
      }
      loadNewData(adId);
    });
  };

  const handlePromoteClick = (adId: string) => {
    history.push(`${PageRoute.promote}/${adId}?${START_PAGE_PARAM}=${PromotionFromPages.MyAds}`);
  }

  const handleAwaitingPaymentClick = () => {
    history.push(PageRoute.finance);
  }

  const handleAutoPromotionEnableClick = (adId: string) => {
    props.enableAdAutoPromotion(adId, props.ads, loadNewData);
  }

  const handleAutoPromotionDisableClick = (adId: string) => {
    props.disableAdAutoPromotion(adId, props.ads, loadNewData);
  }

  const handlePriceChange = (adId: string, module: string, priceData: any) => {
    props.changeAdPrice(adId, priceData, props.ads, (success) => {
      if (success) {
        notification.show("Cijena uspješno promijenjena");
        ReactGA.event({
          category: "Moji oglasi",
          action: "click",
          label: "Izmjena cijene"
        });
      }
      loadNewData(adId);
    });
  };

  const goToPage = (nextPage: number) => {
    const safe = nextPage && nextPage > 0 ? nextPage : 1;
    setPage(safe);
    syncPageToUrl(safe);
  };

  const handlePageChange = (nextPage: number) => {
    goToPage(nextPage);
    loadMyAds(props, nextPage, searchCriteria);
  };

  const handleTabChange = (status: number) => {
    goToPage(1);
    onPromotionStatusSearch(undefined);
    onTabChange(status);
  }

  const searchBarActions = {
    onTextSearch: (...params: [React.ChangeEvent<HTMLInputElement>]) => {
      onTextSearch(...params);
      goToPage(1);
    },
    onModuleSearch: (...params: [string]) => {
      onModuleSearch(...params);
      goToPage(1);
    },
    onPromotionStatusSearch: (value) => {
      onPromotionStatusSearch(value);
      goToPage(pageFromUrl);
    },
    onSortOptionChange: (value) => {
      onSortOptionChange(value);
      goToPage(1);
    }
  };

  useEffect(() => {
    if (ads && ads.actionTime === actionTime) {
      setTempAds(ads);
    }
  }, [ads]);

  useEffect(() => {
    const targetId = pendingScrollAdIdRef.current;
    if (!targetId || !tempAds || !Array.isArray(tempAds.data)) return;
    const inCurrentPage = tempAds.data.some((a: any) => a && a.id === targetId);
    if (!inCurrentPage) {
      // Ad no longer on this page (deleted / moved) — drop the request so
      // subsequent renders don't keep hunting for it.
      pendingScrollAdIdRef.current = null;
      return;
    }
    const el = document.getElementById(`my-ads-card-${targetId}`);
    if (el) {
      // Defer past paint so the newly rendered card has final geometry.
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      pendingScrollAdIdRef.current = null;
    }
  }, [tempAds]);

  return (
    <React.Fragment>
      <SearchBar
        text={searchCriteria.title}
        module={searchCriteria.module}
        modules={modules}
        sortOption={searchCriteria.sortOption}
        AdPromotionFilterOptions={props.AdPromotionFilterOptions}
        adPromotionFilter={searchCriteria.adPromotionFilter}
        status={status}
        {...searchBarActions}
      />
      <div className={`child-center ${styles.adsContainer}`}>
        <PaginationAds
          ads={tempAds}
          handlePageChange={handlePageChange}
          responsiveProps={{ xs: 24, sm: 24 }}
          adCardProps={{
            onEdit: handleEditClick,
            onDelete: handleDeleteClick,
            onActive: handleActiveClick,
            onPaused: handlePausedClick,
            onSold: handleSoldClick,
            onRenew: handleRenewClick,
            onPromote: handlePromoteClick,
            onAwaitingPayment: handleAwaitingPaymentClick,
            onAutoPromotionEnable: handleAutoPromotionEnableClick,
            onAutoPromotionDisable: handleAutoPromotionDisableClick,
            onPriceChange: handlePriceChange,
            showActions: true
          }}
          paginationClassName={styles.pagination}
          onTabChange={handleTabChange}
          filters={props.AdStatusOptions}
          statistic={statistic}
          status={status}
        />
      </div>
    </React.Fragment>
  );
};

TabContent.defaultProps = {
  ads: {},
  status: 0,
};

const ConnectedMyAdsContent = connect(
  (state: rootReducerInterface) => ({
    ads: selectMyAds(state),
    modules: selectModules(state),
  }),
  {
    getMyAds,
    myAdsDeleteAdAction,
    activeAd,
    pausedAd,
    soldAd,
    updateMyAds,
    renewAd,
    enableAdAutoPromotion,
    disableAdAutoPromotion,
    changeAdPrice,
    getUserProfile
  }
  // @ts-ignore
)(TabContent);

export default React.memo(ConnectedMyAdsContent);



// WEBPACK FOOTER //
// ./src/containers/MyAdsPage/MyAds/TabContent/index.tsx